import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Send, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ContactModal = ({ open, onOpenChange, language = 'es', context = '' }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const t = language === 'es' ? {
    title: 'Contáctanos',
    desc: 'Cuéntanos qué necesitas y te responderemos en menos de 24 h.',
    name: 'Nombre completo',
    email: 'Email',
    phone: 'Teléfono (opcional)',
    message: 'Mensaje',
    messagePh: 'Cuéntanos qué necesitas: consulta sobre precios, disponibilidad, etc.',
    send: 'Enviar consulta',
    sending: 'Enviando...',
    close: 'Cerrar',
    sentTitle: '¡Consulta enviada!',
    sentDesc: 'Te responderemos por email en menos de 24 h. Revisa tu bandeja de entrada — ya deberías tener una confirmación.',
    close2: 'Cerrar',
  } : {
    title: 'Contact us', desc: 'Tell us what you need and we\'ll get back within 24 h.',
    name: 'Full name', email: 'Email', phone: 'Phone (optional)', message: 'Message',
    messagePh: 'Tell us what you need: pricing, availability, etc.',
    send: 'Send inquiry', sending: 'Sending...', close: 'Close',
    sentTitle: 'Inquiry sent!', sentDesc: 'We\'ll reply by email within 24 h. Check your inbox for a confirmation.',
    close2: 'Close',
  };

  const reset = () => {
    setForm({ name: '', email: '', phone: '', message: '' });
    setSent(false);
    setSending(false);
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.length < 2 || form.message.length < 5 || !form.email.includes('@')) {
      toast.error(language === 'es' ? 'Completa nombre, email y mensaje' : 'Complete name, email and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact/inquiry`, { ...form, context });
      setSent(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al enviar. Intenta más tarde.' : 'Error sending. Try later.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="contact-modal">
        {sent ? (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-center">{t.sentTitle}</DialogTitle>
              <DialogDescription className="text-center">{t.sentDesc}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => handleClose(false)} className="w-full" data-testid="contact-modal-close">
                {t.close2}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-emerald-600" />
                {t.title}
              </DialogTitle>
              <DialogDescription>{t.desc}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="c-name">{t.name} *</Label>
                <Input
                  id="c-name" required minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label htmlFor="c-email">{t.email} *</Label>
                <Input
                  id="c-email" type="email" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                />
              </div>
              <div>
                <Label htmlFor="c-phone">{t.phone}</Label>
                <Input
                  id="c-phone" type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <Label htmlFor="c-message">{t.message} *</Label>
                <Textarea
                  id="c-message" required minLength={5} rows={4}
                  placeholder={t.messagePh}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={sending}>
                {t.close}
              </Button>
              <Button type="submit" disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="contact-submit">
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {sending ? t.sending : t.send}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
